import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import { Save, Globe, DollarSign, Mail, Shield, Bell, Zap, Database } from 'lucide-react';

interface PlatformSettings {
  // General Settings
  platformName: string;
  platformUrl: string;
  supportEmail: string;

  // Financial Settings
  currency: string;
  defaultCommissionRate: number;
  platformFeePercentage: number;
  minPayoutAmount: number;

  // Email Settings
  emailProvider: string;
  emailFromName: string;
  emailFromAddress: string;

  // Security Settings
  sessionTimeout: number;
  maxLoginAttempts: number;
  requireEmailVerification: boolean;

  // Affiliate Settings
  autoApproveAffiliates: boolean;
  affiliateCommissionWindow: number;

  // Notifications
  notifyOnNewStore: boolean;
  notifyOnNewAffiliate: boolean;
  notifyOnNewOrder: boolean;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<PlatformSettings>({
    platformName: 'Dukene',
    platformUrl: 'https://dukene.com',
    supportEmail: 'support@dukene.com',
    currency: 'KES',
    defaultCommissionRate: 10,
    platformFeePercentage: 2.5,
    minPayoutAmount: 1000,
    emailProvider: 'Resend',
    emailFromName: 'Dukene Platform',
    emailFromAddress: 'noreply@dukene.com',
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    requireEmailVerification: true,
    autoApproveAffiliates: false,
    affiliateCommissionWindow: 30,
    notifyOnNewStore: true,
    notifyOnNewAffiliate: true,
    notifyOnNewOrder: false,
  });

  const { data: fetchedSettings } = useQuery<PlatformSettings>({
    queryKey: ['admin-settings'],
    queryFn: () => apiClient.get('/admin/settings'),
  });

  useEffect(() => {
    if (fetchedSettings) {
      setSettings(prev => ({
        ...prev,
        ...fetchedSettings,
        // Ensure numbers are parsed correctly
        defaultCommissionRate: Number(fetchedSettings.defaultCommissionRate ?? prev.defaultCommissionRate),
        platformFeePercentage: Number(fetchedSettings.platformFeePercentage ?? prev.platformFeePercentage),
        minPayoutAmount: Number(fetchedSettings.minPayoutAmount ?? prev.minPayoutAmount),
        sessionTimeout: Number(fetchedSettings.sessionTimeout ?? prev.sessionTimeout),
        maxLoginAttempts: Number(fetchedSettings.maxLoginAttempts ?? prev.maxLoginAttempts),
        affiliateCommissionWindow: Number(fetchedSettings.affiliateCommissionWindow ?? prev.affiliateCommissionWindow),
        // Ensure booleans are parsed correctly (if they come as strings)
        requireEmailVerification: String(fetchedSettings.requireEmailVerification) === 'true' || fetchedSettings.requireEmailVerification === true,
        autoApproveAffiliates: String(fetchedSettings.autoApproveAffiliates) === 'true' || fetchedSettings.autoApproveAffiliates === true,
        notifyOnNewStore: String(fetchedSettings.notifyOnNewStore) === 'true' || fetchedSettings.notifyOnNewStore === true,
        notifyOnNewAffiliate: String(fetchedSettings.notifyOnNewAffiliate) === 'true' || fetchedSettings.notifyOnNewAffiliate === true,
        notifyOnNewOrder: String(fetchedSettings.notifyOnNewOrder) === 'true' || fetchedSettings.notifyOnNewOrder === true,
      }));
    }
  }, [fetchedSettings]);

  const [saveMessage, setSaveMessage] = useState('');

  const saveMutation = useMutation({
    mutationFn: (data: PlatformSettings) => apiClient.put('/admin/settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      setSaveMessage('Settings saved successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    },
    onError: () => {
      setSaveMessage('Failed to save settings');
    }
  });

  const handleSave = () => {
    saveMutation.mutate(settings);
  };

  const updateSetting = (key: keyof PlatformSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-black">Platform Settings</h1>
          <p className="text-gray-500 mt-2">Configure global platform settings and preferences</p>
        </div>

        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-bold"
        >
          <Save className="w-5 h-5" />
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`p-4 rounded-lg border ${saveMessage.includes('success')
          ? 'bg-gray-50 border-gray-200 text-gray-900'
          : 'bg-red-50 border-red-200 text-red-900'
          }`}>
          <p className="font-medium text-sm">{saveMessage}</p>
        </div>
      )}

      {/* General Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
            <Globe className="w-6 h-6 text-gray-700" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">General Settings</h2>
            <p className="text-sm text-gray-500">Basic platform configuration</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-black mb-2">Platform Name</label>
            <input
              type="text"
              value={settings.platformName}
              onChange={(e) => updateSetting('platformName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">Platform URL</label>
            <input
              type="url"
              value={settings.platformUrl}
              onChange={(e) => updateSetting('platformUrl', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">Support Email</label>
            <input
              type="email"
              value={settings.supportEmail}
              onChange={(e) => updateSetting('supportEmail', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Financial Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">Financial Settings</h2>
            <p className="text-sm text-gray-500">Currency and payment configuration</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-bold text-black mb-2">Currency</label>
            <select
              value={settings.currency}
              onChange={(e) => updateSetting('currency', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            >
              <option value="KES">KES - Kenyan Shilling</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">Default Commission Rate (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.defaultCommissionRate}
              onChange={(e) => updateSetting('defaultCommissionRate', parseFloat(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">Minimum Payout Amount</label>
            <input
              type="number"
              min="0"
              value={settings.minPayoutAmount}
              onChange={(e) => updateSetting('minPayoutAmount', parseFloat(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">Platform Fee (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.platformFeePercentage}
              onChange={(e) => updateSetting('platformFeePercentage', parseFloat(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-gray-700" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">Email Settings</h2>
            <p className="text-sm text-gray-500">Email service configuration</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-black mb-2">Email Provider</label>
            <select
              value={settings.emailProvider}
              onChange={(e) => updateSetting('emailProvider', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            >
              <option value="Resend">Resend</option>
              <option value="SendGrid">SendGrid</option>
              <option value="Mailgun">Mailgun</option>
              <option value="SMTP">Custom SMTP</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-black mb-2">From Name</label>
              <input
                type="text"
                value={settings.emailFromName}
                onChange={(e) => updateSetting('emailFromName', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-black mb-2">From Email Address</label>
              <input
                type="email"
                value={settings.emailFromAddress}
                onChange={(e) => updateSetting('emailFromAddress', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">Security Settings</h2>
            <p className="text-sm text-gray-500">Authentication and security configuration</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-black mb-2">Session Timeout (minutes)</label>
              <input
                type="number"
                min="5"
                max="1440"
                value={settings.sessionTimeout}
                onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-black mb-2">Max Login Attempts</label>
              <input
                type="number"
                min="3"
                max="10"
                value={settings.maxLoginAttempts}
                onChange={(e) => updateSetting('maxLoginAttempts', parseInt(e.target.value))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="requireEmailVerification"
              checked={settings.requireEmailVerification}
              onChange={(e) => updateSetting('requireEmailVerification', e.target.checked)}
              className="w-5 h-5 rounded border-2 border-gray-300 text-black focus:ring-2 focus:ring-black"
            />
            <label htmlFor="requireEmailVerification" className="flex-1 cursor-pointer">
              <p className="font-bold text-sm text-black">Require Email Verification</p>
              <p className="text-xs text-gray-600">Users must verify their email before accessing the platform</p>
            </label>
          </div>
        </div>
      </div>

      {/* Affiliate Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-gray-700" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">Affiliate Settings</h2>
            <p className="text-sm text-gray-500">Affiliate program configuration</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-black mb-2">Commission Attribution Window (days)</label>
            <input
              type="number"
              min="1"
              max="90"
              value={settings.affiliateCommissionWindow}
              onChange={(e) => updateSetting('affiliateCommissionWindow', parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            />
            <p className="text-xs text-gray-500 mt-2">Number of days after a click that a sale can be attributed to an affiliate</p>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="autoApproveAffiliates"
              checked={settings.autoApproveAffiliates}
              onChange={(e) => updateSetting('autoApproveAffiliates', e.target.checked)}
              className="w-5 h-5 rounded border-2 border-gray-300 text-black focus:ring-2 focus:ring-black"
            />
            <label htmlFor="autoApproveAffiliates" className="flex-1 cursor-pointer">
              <p className="font-bold text-sm text-black">Auto-Approve Affiliates</p>
              <p className="text-xs text-gray-600">Automatically approve new affiliate applications without manual review</p>
            </label>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">Notification Settings</h2>
            <p className="text-sm text-gray-500">Configure admin notifications</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="notifyOnNewStore"
              checked={settings.notifyOnNewStore}
              onChange={(e) => updateSetting('notifyOnNewStore', e.target.checked)}
              className="w-5 h-5 rounded border-2 border-gray-300 text-black focus:ring-2 focus:ring-black"
            />
            <label htmlFor="notifyOnNewStore" className="flex-1 cursor-pointer">
              <p className="font-bold text-sm text-black">New Store Registrations</p>
              <p className="text-xs text-gray-600">Receive notifications when a new store is created</p>
            </label>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="notifyOnNewAffiliate"
              checked={settings.notifyOnNewAffiliate}
              onChange={(e) => updateSetting('notifyOnNewAffiliate', e.target.checked)}
              className="w-5 h-5 rounded border-2 border-gray-300 text-black focus:ring-2 focus:ring-black"
            />
            <label htmlFor="notifyOnNewAffiliate" className="flex-1 cursor-pointer">
              <p className="font-bold text-sm text-black">New Affiliate Applications</p>
              <p className="text-xs text-gray-600">Receive notifications when someone applies to be an affiliate</p>
            </label>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="notifyOnNewOrder"
              checked={settings.notifyOnNewOrder}
              onChange={(e) => updateSetting('notifyOnNewOrder', e.target.checked)}
              className="w-5 h-5 rounded border-2 border-gray-300 text-black focus:ring-2 focus:ring-black"
            />
            <label htmlFor="notifyOnNewOrder" className="flex-1 cursor-pointer">
              <p className="font-bold text-sm text-black">New Orders</p>
              <p className="text-xs text-gray-600">Receive notifications for every order placed on the platform</p>
            </label>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
            <Database className="w-6 h-6 text-gray-700" />
          </div>
          <div>
            <h2 className="text-xl font-black text-black">System Information</h2>
            <p className="text-sm text-gray-500">Platform version and technical details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Platform Version</p>
            <p className="text-lg font-black text-black">v1.0.0</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Database</p>
            <p className="text-lg font-black text-black">PostgreSQL</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Cache</p>
            <p className="text-lg font-black text-black">Redis</p>
          </div>
        </div>
      </div>
    </div>
  );
}
